/**
 * W2-F (M19): per-resource optimistic-concurrency version cache for the
 * builder save paths (document/wallet/calendar/email templates, portal
 * config, template-scoped twins).
 *
 * The backend `$inc`s a `version` counter on every update and honors an
 * `If-Match: <version>` precondition (409 `versionMismatch` on a stale write —
 * same contract as website-config, audit CRIT-6). The clients remember the
 * version seen on GET here and echo it on PUT. Keyed by the resource's own
 * ObjectId hex (globally unique), so one map serves every collection.
 *
 * Reads and writes overlap (wallet wave 03.11.2026). A save invalidates its
 * queries, a window refocus refetches, a translate reloads — so a GET is often
 * in flight across a PUT, and its answer can describe the row BEFORE that
 * write. Two rules keep the cache on the server's value:
 *
 *  - A successful PUT SETTLES the version from what it sent: the server is now
 *    exactly `sent + 1`. Not "local + 1" — a GET that already brought `sent + 1`
 *    in during the flight would otherwise push it to `sent + 2`, and the next
 *    save 409s against the operator's own write.
 *  - A GET takes a TICKET (the id's completed-write count) before it leaves.
 *    If a PUT completed in between, its answer may predate that write: it may
 *    only RAISE the cached version, never lower it. A current ticket
 *    overwrites — a lower value is legitimate there (the portal translate
 *    rewrites the row and its version, `TemplateTranslateJobService`).
 */
const versions = new Map<string, number>();
/** Completed PUTs per id — the ticket clock. */
const writes = new Map<string, number>();

/** Take before a GET's `await`; hand to `rememberBuilderVersion` after it. */
export function builderReadTicket(id: string | undefined): number {
  if (!id) return 0;
  return writes.get(id) ?? 0;
}

/**
 * Remember the version a GET returned. With a `ticket` from before the
 * request, an answer that a PUT has overtaken only raises the cached value
 * (and leaves "never seen" as it is — the next PUT then goes without
 * If-Match, last-write-wins, as for a legacy row). Without a ticket: plain
 * overwrite, as before.
 */
export function rememberBuilderVersion(id: string | undefined, version: unknown, ticket?: number): void {
  if (!id) return;
  if (typeof version !== "number" || !Number.isFinite(version)) return;
  if (ticket !== undefined && ticket !== (writes.get(id) ?? 0)) {
    const cur = versions.get(id);
    if (cur !== undefined && version > cur) versions.set(id, version);
    return;
  }
  versions.set(id, version);
}

/** Version to echo as If-Match, or undefined when we never saw one
 *  (legacy row / GET raced) — then the PUT stays last-write-wins. */
export function builderVersionFor(id: string | undefined): number | undefined {
  if (!id) return undefined;
  return versions.get(id);
}

/** The If-Match header for the version a PUT is about to send — read the
 *  version ONCE (`builderVersionFor`) and pass the same value to
 *  `settleBuilderVersion`, so header and settlement cannot disagree. */
export function builderIfMatchFrom(sent: number | undefined): Record<string, string> {
  return sent === undefined ? {} : { "If-Match": String(sent) };
}

/**
 * Call after a PUT succeeded, with the version it SENT. A guarded write leaves
 * the server at exactly `sent + 1`; an unguarded one (`sent` undefined) tells
 * us nothing about the number, so only the write clock moves.
 */
export function settleBuilderVersion(id: string | undefined, sent: number | undefined): void {
  if (!id) return;
  writes.set(id, (writes.get(id) ?? 0) + 1);
  if (sent !== undefined) versions.set(id, sent + 1);
}

/** Test seam: forget everything. */
export function __resetBuilderVersionsForTest(): void {
  versions.clear();
  writes.clear();
}
